import {PolymerElement, html} from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-card/paper-card.js';
import '@cbar/cbar-shared-styles/cbar-shared-styles.js';
import '@cbar/cbar-progress-bar/cbar-progress-bar.js';
import '@polymer/iron-icons/social-icons.js';
import '@polymer/iron-icon/iron-icon.js';
import '@polymer/paper-button/paper-button.js';
import '@cbar/localizemixin/LocalizeMixin.js';


/**
 * `cbar-cf-card` is a card being used to present project funding progress and gives a general view of the project. This element currently grabs an object and all the information in that
 * object is used to populate the card with its name, description, percentage bar, links, icon, and background image, this allows for a very customisable experience with the card but also
 * allows for a standardised look for each of the cards that are generated.
 *
 * The information is passed in through the property: `crowd-data` and can be passed in with an iron-ajax element or any element that allows for an object full of information
 * to be generated. However the naming convention of the information must match the following to be displayed properly (can be formatted before importing externally).
 *
 * - For the title :                name
 * - For the description:           description
 * - for the image:                 image
 * - for the icon:                  icon
 * - for the percentage bar:        gained, needed
 * - for the number of supporters:  supporterCount
 * - for the link                   url
 * - for calculating the days left: dueDate
 * - for the caption:               caption
 * - for the minimum input:         minimum
 *
 * if these items are not passed in the object then the element WILL NOT DISPLAY one or more parts of the element, however, if this reaction is desired to be so, leaving these out
 * may be a way to do so.
 *
 *
 *
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarCfCard extends Cbar.LocalizeMixin(PolymerElement) {
  static get template() {
    return html`
        <style include="cbar-shared-styles">
            :host {
                display: block;
                --paper-card-header-image-text: {
                    top: 0;
                    right: 0;
                    bottom: initial;
                    background-color: var(--cbar-sys-color-primary, #004CA2);
                    color: var(--cbar-icon-text, white);
                    font-weight: 200;
                    font-size: 16px;
                };
                --paper-card-header-image: {
                    height: 175px;
                };

            }

            paper-card {
                width: 100%;
                height: 100%;
            }

            h6 {
                color: var(--cbar-sys-color-on-surface, black);
                display: inline-block;
                margin-bottom: 1%;
                margin-top: 0;
                width: 85%;
            }

            cbar-progress-bar{
                --cbar-progress-height: 10px;
                margin-top: 10px;
                --progress-bar-color: var(--cbar-sys-color-secondary, #003F83);
            }

            .progress-details {
                color: var(--cbar-secondary-text-color, #757575);
            }

            .top-details {
                margin-top: 5%;
                font-size: 20px;
            }

            .days-remaining {
                color: var(--cbar-secondary-text-color, #757575);
                margin-bottom: 2%;
                margin-top: 1%;
                text-align: right;
            }

            .title-div {
                display: flex;
                justify-content: space-between;
                width: 100%;
                margin-bottom: 10px;
            }

            .mindiv {
                float: right;
                height: 33px;
                color: var(--cbar-sys-color-on-secondary-container, var(--cbar-icon-text, white));
                background-color: var(--cbar-secondary-container-color, var(--cbar-dark-primary-color, #003F83));
                display: flex;
                justify-content: center;
                align-items: center;

            }

            .mindiv-sub {
                color: var(--cbar-secondary-text-color, #757575);
                float: right;
            }

            iron-icon {
                --iron-icon-width: 20px;
                --iron-icon-height: 20px;
                margin-top: -4px;

            }

            .group-icon {
                position: absolute;
                margin-top: -140px;
                margin-left: 2%;
            }

            .group-icon > a > img {
                width: 100px;
                height: 100px;
                border-radius: 50%;
                border: 2px solid rgba(255, 255, 255, .5);
            }

            .bottom-button {
                margin-top: 3%;
                width: 100%;
                background-color: var(--cbar-sys-color-primary-container, var(--cbar-accent-color, #FFC107));
                color: var(--cbar-sys-color-on-primary-container(var(--cbar-icon-text, white));
            }

            .card-content{
                height: calc(100% - 207px);
                display: flex;
                flex-direction: column;
                justify-content: space-between;
            }
            .minsection{
            display: flex;
            flex-direction: column;
            width: 65px;
            text-align: center;
            }
        </style>
        
        <paper-card image="[[crowdData.image]]" heading="[[crowdData.caption]]">
            <template is="dom-if" if="[[crowdData.icon]]">
                <div class="group-icon"><!-- TODO: Find out desired link method with andrew, work this out--><a href="[[crowdData.link]]"><img src="[[crowdData.icon]]"></a></div>
            </template>

            <div class="card-content">
                <div class="top-section">
                <div class="title-div">
                    <h6>[[crowdData.title]]</h6>
                    <template is="dom-if" if="[[crowdData.minimum]]">
                    <div class="minsection">
                        <div class="mindiv">[[crowdData.minimum]]</div>
                        <div class="mindiv-sub">Min</div>
                    </div>
                    </template>
                </div>
                <div class="slim-font small-font spacing description"></div>
                </div>
                <div class="bottom-section">
                <template is="dom-if" if="[[moneyNeededCurrency]]">
                    <div class="progress-div">
                        <div class="progress-details top-details">
                        [[currencySymbol]] [[moneyMadeCurrency]] / <iron-icon icon="social:person-outline" title="[[crowdData.investorType]]"></iron-icon>
                            [[crowdData.supporterCount]]
                        </div>
                        <div class="progress-details">
                            <span>of </span>[[currencySymbol]][[ moneyNeededCurrency]]
                        </div>
                        <cbar-progress-bar percentage="[[progress]]">
                            <slot name="inner-bar-slot" slot="inner-bar-slot"></slot>
                        </cbar-progress-bar>
                        <div class="days-remaining" id="daysRemaining" hidden\$="[[!daysRemaining]]">[[daysRemaining]] days left</div>
                    </div>
                </template>
                <paper-button class="bottom-button" on-tap="_redirectToHref" id="actionButton" hidden\$="[[_finished]]">
                </paper-button>
                </div>
            </div>
        </paper-card>
    `;
  }

  static get is() {
    return 'cbar-cf-card';
  }

  static get properties() {
    return {
      /**
       * Property used for testing elements with default values, unusable otherwise
       */
      _name: {
        type: String,
        value: 'cbar-cf-card'
      },
      /**
       * Object that is passable into the element, represents the data that will show inside the element (Check Description)
       * When this property changes, it will calculate the progress bar of the element, and will display the calculated values being called from this observer
       */
      crowdData: {
        type: Object,
        observer: "_setupData"
      },
      _finished: Boolean,
      currencySymbol: {
        type: String,
        value: "$"
      },
      imageSizing: {
        type: String,
        value: "cover"
      }
    };
  }

  /**
   * Standard ready function with added hacked function
   * The progress bar element only allows for animated to be set, however we did not want this behavior in our element, so we hacked a way into stopping that from happening
   */
  ready() {
    super.ready();
    this._setImageSizing();
  }

  static get observers() {
    return [
      '_translateActionButton(language)'
    ]
  }

  get supportButton() {
    if (!this.__supportButton) {
      this.__supportButton = this.root.querySelector('.bottom-button');
    }
    return this.__supportButton;
  }

  _setupData(data) {
    if (!data) {
      return;
    }

    if (!data.image) {
      this.crowdData.image = "";
    }

    this._calculateProgress();
  }


  _translateActionButton() {
    if(!this.crowdData){
      return;
    }
    requestAnimationFrame(() => {
      // this will return empty string, we want to show the un-translated version of the word so at least something displays
      let translatedButtonText = this.localize(this.crowdData.actionText);
      //NOTE: maybe we should put a console warn in about this?
      this.supportButton.innerHTML = translatedButtonText ? translatedButtonText : this.crowdData.actionText;
    });
  }

  /**
   * Calculates the percentage of the bar that should be filled, this is in order to allow for the user to be able to see the percentage of the money
   * that has been pitched to the project, this also parses these money values into the next function for further calculations
   * @private
   */

  _calculateProgress() {
    let progressBar = this.$.progress;
    let data = this.crowdData;
    this._formatDescription(data.description);
    let moneyMade = data.gained;
    let moneyNeeded = data.needed;
    let percentageMade = moneyMade / moneyNeeded * 100;

    if (percentageMade > 100) {
      percentageMade = 100;
    }

    this.progress = percentageMade;
    this._calculateProgressDetails(moneyMade, moneyNeeded, data);
    this._calculateDescriptionWidth();
  }

  /**
   * Allows the description to have html tags in it in order to format the description in the way people want
   */
  _formatDescription(description) {
    let descriptionDiv = this.root.querySelector('.description');
    descriptionDiv.innerHTML = description;
  }

  /**
   * If there is a minimum that is passed into the element, we dont want the description to overlap the minimum div that we will show,
   * so this makes sure that doesnt happen.
   */
  _calculateDescriptionWidth() {
    if (this.crowdData.minimum) {
      this.root.querySelector(".description").style.width = "90%";
    }
  }

  /**
   * takes in 3 params used for the calculation of the data that surrounds the progress bar including days remaining, and the amount of money pledged from so many
   * investors out of the goal that they wish to be aiming for.
   *
   * @param moneyMade
   * @param moneyNeeded
   * @param data
   * @private
   */

  _calculateProgressDetails(moneyMade, moneyNeeded, data) {

    if (!moneyNeeded) {
      this._finished = false;
      return;
    }

    const oneDay = 1000 * 60 * 60 * 24;

    //TODO discuss backend going to be used, if backend has convert to currency option, dont worry about setting this at the backend should handle it.
    this.moneyMadeCurrency = moneyMade;
    this.moneyNeededCurrency = moneyNeeded;

    let todaysDate = new Date();
    let dueDate = new Date(data.dueDate);

    let msDifference = dueDate.getTime() - todaysDate.getTime();
    let remainingDays = Math.round(msDifference / oneDay);
    this.daysRemaining = remainingDays;

    requestAnimationFrame(() => {
      if (moneyMade === moneyNeeded || remainingDays <= 0 || !data.actionText) {
        this._finished = true;
        this.root.querySelector('#daysRemaining').innerHTML = "Finished";
      }
    });
  }

  _redirectToHref() {
    location.href = this.crowdData.link;
  }

  _setImageSizing() {
    let card = this.root.querySelector('paper-card');
    let image = card.root.querySelector('iron-image');

    image.sizing = this.imageSizing;
  }
}

window.customElements.define(CbarCfCard.is, CbarCfCard);
